<template>
    <form-wrapper @form-submit="submit">
        <form-row>
            <form-cell>
                <text-input
                    :formData="data"
                    :label="$t('forms.company.create_company.fields.name.label')"
                    :placeholder="$t('pages.company.custom_reminders.row_title')"
                    :isRequired="true"
                    type="text"
                    valuePath="reminderName"
                    @change="change"
                ></text-input>
            </form-cell>
        </form-row>
        <form-row>
            <form-cell>
                <textarea-input
                    :formData="data"
                    :label="$t('forms.contact.contact.fields.description.label')"
                    :placeholder="$t('forms.contact.contact.fields.description.placeholder')"
                    :isRequired="true"
                    valuePath="reminderMessage"
                    @input="messageChange($event.target.value)"
                    @change="change"
                ></textarea-input>
                <p v-if="showPriceRSD">
                    {{
                        `${$t('pages.company.custom_reminders.price')}: ${priceRSD} ${$t(
                            'pages.company.custom_reminders.info_modal.price',
                        )}`
                    }}
                </p>
                <p v-else>{{ `${$t('pages.company.custom_reminders.price_bam')}: ${priceBAM}` }}</p>
            </form-cell>
        </form-row>

        <form-row>
            <form-cell>
                <checkbox-input
                    :formData="data"
                    :label="$t('pages.company.custom_reminders.show_time_example')"
                    :value="data.includeCalendarEventInfo"
                    valuePath="includeCalendarEventInfo"
                    @change="changeIncludeCalendarEventInfo"
                ></checkbox-input>
            </form-cell>
        </form-row>

        <form-row>
            <form-cell width="half">
                <select-input
                    :formData="data"
                    :label="$t('pages.company.custom_reminders.send_before')"
                    :placeholder="$t('forms.offer.create_offer.fields.appointment_length.placeholder')"
                    :isRequired="true"
                    :options="appointmentLengthOptions"
                    valuePath="remindBeforeMinutes"
                    @change="changeRemindBeforeMinutes"
                ></select-input>
                <p class="custom-reminder--info-remind">{{ infoRemindBeforeMinutes }}</p>
            </form-cell>
            <form-cell width="half">
                <multi-select-input
                    :formData="data"
                    :label="$t('forms.offer.discount_offer.offer.label')"
                    :placeholder="$t('forms.offer.discount_offer.offer.placeholder')"
                    :options="offersOptions"
                    :isRequired="true"
                    valuePath="offerIds"
                    :showSelectAllOption="true"
                    @change="change"
                ></multi-select-input>
            </form-cell>
        </form-row>
        <form-row v-if="showTypeOptions">
            <form-cell width="full" class="custom-reminder--type">
                <p class="custom-reminder--type__label">{{ $t('pages.company.custom_reminders.chose_type') }}:</p>
                <radio-input
                    valuePath="type"
                    :isRequired="true"
                    :direction="radioInputType"
                    :options="typeOptions"
                    :value="data.type"
                    @change="setRadioValue"
                ></radio-input>
            </form-cell>
        </form-row>

        <template #footer>
            <common-buttons>
                <common-button
                    type="primary"
                    :isSubmittable="false"
                    :isProcessing="isLoading"
                    :label="$t('forms.user.profile.buttons.save')"
                    @click="submit()"
                ></common-button>
            </common-buttons>
        </template>
    </form-wrapper>
</template>

<script>
    import FormMixin from 'mixins/FormMixin'
    import CustomReminderLengthEnum from 'enums/custom_reminder_length.enum'
    import CustomReminderTypeEnum from 'enums/custom_reminder_type.enum'
    import RadioInput from 'components/form-inputs/RadioInput'
    import WindowResizeMixin from 'mixins/WindowResizeMixin'
    import CompanyAPI from 'api/CompanyAPI'

    export default {
        mixins: [FormMixin, WindowResizeMixin],

        components: { RadioInput },

        props: {
            offers: { type: Array, required: true },
            prefill: { type: Object, default: undefined },
            company: { type: Object, required: true },
            showPriceRSD: { type: Boolean, default: true },
        },

        data() {
            return {
                selectedType: null,
                priceRSD: 0,
                priceBAM: 0,
                smsPriceRSD: 0,
                smsPriceBAM: 0,
                emailPriceRSD: env('VUE_APP_EMAIL_PRICE_RSD'),
                emailPriceBAM: env('VUE_APP_EMAIL_PRICE_BAM'),
                infoRemindBeforeMinutes: this.$t('pages.company.custom_reminders.info_reminder_before_minutes'),
            }
        },

        computed: {
            appointmentLengthOptions() {
                return CustomReminderLengthEnum[this.$i18n.locale].sort((a, b) => a.value - b.value)
            },

            offersOptions() {
                return this.offers
                    ?.filter((item) => item.offerCategories.length)
                    .map((offer) => {
                        return {
                            label: offer.titleTextContent.originalText,
                            value: offer.id,
                        }
                    })
            },

            typeOptions() {
                return Object.values(CustomReminderTypeEnum)
                    .filter((cusRemind) => cusRemind.value !== CustomReminderTypeEnum.FREE_EMAIL.value)
                    .map((cusRemind) => ({
                        label: cusRemind.label,
                        value: cusRemind.value,
                    }))
            },

            radioInputType() {
                return this.isTabletSmall ? 'two-columns' : 'row'
            },

            showTypeOptions() {
                return this.prefill.type !== CustomReminderTypeEnum.FREE_EMAIL.value
            },
        },

        methods: {
            messageChange(message) {
                const value = message
                var smsCount = this.countSMSParts(value)

                this.smsPriceRSD = env('VUE_APP_SMS_PRICE_RSD') * smsCount
                this.smsPriceBAM = env('VUE_APP_SMS_PRICE_BAM') * smsCount

                this.customReminderPriceRSD()
                this.customReminderPriceBAM()
            },

            submit() {
                this.data.includeCalendarEventInfo = !!this.data.includeCalendarEventInfo
                this.data.remindBeforeMinutes = Number(this.data.remindBeforeMinutes)
                if (!this.data.offerIds || this.data.offerIds.length === 0) {
                    this.$toast.error(this.$t('pages.company.custom_reminders.select_offers_msg'))
                    return
                } else if (Number.isNaN(this.data.remindBeforeMinutes)) {
                    this.$toast.error(this.$t('pages.company.custom_reminders.select_remind_before_msg'))
                    return
                }

                if (this.data.id) {
                    this.submitForm(CompanyAPI.editCustomReminders(this.company?.id, this.data.id, this.data))
                        .then((res) => {
                            this.$toast.success(this.$t('common.microcopy.success'))

                            this.emitEvent({ name: 'custom-reminder-changed', payload: res })
                        })
                        .catch(() => {
                            this.$toast.error(this.$t('common.errors.common_error'))
                        })
                } else {
                    this.submitForm(CompanyAPI.createCustomReminders(this.company?.id, this.data))
                        .then((res) => {
                            this.$toast.success(this.$t('common.microcopy.success'))
                            this.emitEvent({ name: 'custom-reminder-changed', payload: res })
                        })
                        .catch(() => {
                            this.$toast.error(this.$t('common.errors.common_error'))
                        })
                }
            },

            changeIncludeCalendarEventInfo(event) {
                this.data.includeCalendarEventInfo = !!event.value
                this.messageChange(this.data.reminderMessage)
            },

            setRadioValue({ value }) {
                if (value) {
                    this.radioValue = value
                    this.data.type = value
                    this.customReminderPriceRSD()
                    this.customReminderPriceBAM()
                }
            },

            countSMSParts(message) {
                const GSM_7BIT_REGEX =
                    // eslint-disable-next-line no-control-regex
                    /^[\x20-\x7E\r\n\x0A\x0D\x0B\x0C\x1B\x24\x40\xA3\xA5\xC0\xC3-\xC5\xC7\xC9\xD1\xD3-\xD6\xD8\xDC\xE0\xE3-\xE5\xE7\xE9\xF1\xF3-\xF6\xF8\xFC]*$/

                const isGSM7Bit = GSM_7BIT_REGEX.test(message)
                let length = message.length

                if (this.data.includeCalendarEventInfo) {
                    length += 21
                }

                if (isGSM7Bit) {
                    return length <= 160 ? 1 : Math.ceil(length / 153)
                } else {
                    return length <= 70 ? 1 : Math.ceil(length / 67)
                }
            },

            customReminderPriceRSD() {
                var price = 0
                if (
                    this.radioValue === CustomReminderTypeEnum.SMS.value ||
                    this.radioValue === CustomReminderTypeEnum.EMAIL_AND_SMS.value
                ) {
                    price += Number(this.smsPriceRSD)
                }

                if (
                    this.radioValue === CustomReminderTypeEnum.EMAIL.value ||
                    this.radioValue === CustomReminderTypeEnum.EMAIL_AND_SMS.value
                ) {
                    price += Number(this.emailPriceRSD)
                }

                this.priceRSD = price
            },

            customReminderPriceBAM() {
                var price = 0
                if (
                    this.radioValue === CustomReminderTypeEnum.SMS.value ||
                    this.radioValue === CustomReminderTypeEnum.EMAIL_AND_SMS.value
                ) {
                    price += Number(this.smsPriceBAM)
                }

                if (
                    this.radioValue === CustomReminderTypeEnum.EMAIL.value ||
                    this.radioValue === CustomReminderTypeEnum.EMAIL_AND_SMS.value
                ) {
                    price += Number(this.emailPriceBAM)
                }

                this.priceBAM = price.toFixed(2)
            },

            changeRemindBeforeMinutes(event) {
                this.change(event)
                if (this.data.remindBeforeMinutes === '0') {
                    this.infoRemindBeforeMinutes = this.$t('pages.company.custom_reminders.info_reminder_before_minutes_now')
                } else {
                    this.infoRemindBeforeMinutes = this.$t('pages.company.custom_reminders.info_reminder_before_minutes')
                }
            },
        },

        mounted() {
            if (this.prefill && this.prefill?.id) {
                this.prefillData(this.prefill)
                if (this.prefill.remindBeforeMinutes === 0) {
                    this.data.remindBeforeMinutes = '0'
                    this.infoRemindBeforeMinutes = this.$t('pages.company.custom_reminders.info_reminder_before_minutes_now')
                }
                this.radioValue = this.prefill.type
                this.messageChange(this.prefill.reminderMessage)
            }
        },
    }
</script>

<style lang="scss">
    @import 'styles/setup/_variables.scss';
    @import 'styles/setup/_mixins.scss';

    .custom-reminder {
        &--type {
            &__label {
                margin-bottom: 5px;
            }
        }
        &--info-remind {
            margin-bottom: 5px;
            font-size: $font-size--hint;
            color: $color--txt-base-3;
        }
    }
</style>
